import React from 'react';
import {modalConfirm} from 'app/utils/modals/popups';
import I18n from 'app/i18n';
import csrfToken from 'app/helpers/csrf-token';
import {configStore, userStore} from 'app/store/global';
import {locationQuery} from 'app/utils/navigate/location';
import {toastSuccess, toastError} from 'app/components/toasts/liteToast';
import Post from 'app/utils/Post';
import Delete from 'app/utils/Delete';
import Get from 'app/utils/Get';
import logEvent from 'app/amplitude/log_event';
import {pushAnalyticsEvent} from 'app/utils/analytics';

const STEAM_AUTH_FORM_TARGET_TOP = '_top';

interface StartSteamAuthFlowOptions {
    redirectUrl?: string;
    promocode?: string;
    siteLang?: string;
    target?: string;
}

export const startSteamAuthFlow = ({
    redirectUrl,
    promocode,
    siteLang,
    target = STEAM_AUTH_FORM_TARGET_TOP
}: StartSteamAuthFlowOptions = {}): void => {
    const form = document.createElement('form');

    form.method = 'post';
    form.action = '/auth/steam';
    form.target = target;

    const fields: Record<string, string> = {
        authenticity_token: csrfToken() || '',
        site_lang: siteLang || configStore.CONFIG?.lang || 'en'
    };

    if (redirectUrl) {
        fields.redirect_url = redirectUrl;
    }

    if (promocode) {
        fields.promocode = promocode;
    }

    Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');

        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    form.remove();
};

interface PendingLinkData {
    success?: boolean
    steam_id?: string
    nickname?: string | null
    avatar?: string | null
    error?: string
}

const REGISTRATION_ERROR_KEYS = [
    'steam_id_required',
    'steam_already_linked',
    'failed_to_authenticate',
    'steam_unavailable'
];

export const steamRegistrationErrorMessage = (key: string): string => {
    if (REGISTRATION_ERROR_KEYS.includes(key)) {
        return I18n.t(`steam.errors.${key}`);
    }

    return I18n.t('steam.errors.failed_to_authenticate');
};

export const showSteamAuthError = (): void => {
    const params = locationQuery();
    const errorKey = params.steam_auth_error;

    if (!errorKey) {
        return;
    }

    const url = new URL(window.location.href);

    url.searchParams.delete('steam_auth_error');
    window.history.replaceState({}, '', url.toString());

    toastError(steamRegistrationErrorMessage(errorKey));
};

export const showSteamRegistrationSuccess = (): void => {
    const params = locationQuery();

    if (!params.steam_registration) {
        return;
    }

    const url = new URL(window.location.href);

    url.searchParams.delete('steam_registration');
    window.history.replaceState({}, '', url.toString());

    logEvent('REGISTRATION_FORM_SENT', {method: 'steam'});
    pushAnalyticsEvent('registration_complete', {
        method: 'steam',
        placement: 'registration_popup'
    });
};

const ERROR_KEYS = [
    'failed_to_link_account',
    'steam_already_linked',
    'user_already_linked',
    'steam_unavailable',
    'invalid_or_expired_link'
];

const steamErrorMessage = (key: string): string => {
    if (ERROR_KEYS.includes(key)) {
        return I18n.t(`steam.errors.${key}`);
    }

    return I18n.t('steam.errors.failed_to_link_account');
};

const fetchPendingLinkData = (): Promise<PendingLinkData> => new Get({url: '/steam/pending_link'})
    .execute()
    .then(response => response.json());

const clearSteamLinkQueryParams = (): void => {
    const url = new URL(window.location.href);

    url.searchParams.delete('steam_link_pending');
    url.searchParams.delete('steam_link_error');
    window.history.replaceState({}, '', url.toString());
};

export const showSteamLinkConfirmation = (steamLinked: boolean): void => {
    const params = locationQuery();
    const pending = params.steam_link_pending;
    const errorKey = params.steam_link_error;

    if (!pending && !errorKey) {
        return;
    }

    clearSteamLinkQueryParams();

    if (errorKey) {
        toastError(steamErrorMessage(errorKey));
        return;
    }

    if (steamLinked) {
        toastError(I18n.t('steam.errors.user_already_linked'));
        return;
    }

    fetchPendingLinkData()
        .then((data: PendingLinkData) => {
            if (!data.success) {
                toastError(data.error || I18n.t('steam.errors.invalid_or_expired_link'));
                return;
            }

            showPendingConfirmPopup(data);
        })
        .catch(() => {
            toastError(I18n.t('steam.errors.steam_unavailable'));
        });
};

const showPendingConfirmPopup = (data: PendingLinkData): void => {
    const content = {
        element: <div className="steam-link-confirm">
            {data.avatar && <img
                src={data.avatar}
                alt={data.nickname || 'Steam'}
                width={64}
                height={64}
            />}
            <p>{I18n.t('steam.labels.confirm_link_account')}</p>
            {data.nickname && <p><strong>{data.nickname}</strong></p>}
        </div>,
        react: true
    };

    modalConfirm(
        content,
        () => confirmSteamLink(),
        () => cancelSteamLink()
    );
};

const confirmSteamLink = (): void => {
    new Post({url: '/steam/link_account'})
        .execute()
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                toastSuccess(data.message || I18n.t('steam.success.account_linked'));
                userStore.updateProfile();
            } else {
                toastError(data.error || I18n.t('steam.errors.failed_to_link_account'));
            }
        })
        .catch(() => {
            toastError(I18n.t('steam.errors.failed_to_link_account'));
        });
};

const cancelSteamLink = (): void => {
    new Delete({url: '/steam/cancel_link'})
        .execute()
        .catch(() => null);
};
